import { useState, useCallback, useRef, useEffect, useMemo, type JSX, type ChangeEvent } from 'react'
import {
  RefreshCw, MessageSquarePlus, Plus, Search, ChevronLeft, ChevronRight,
  ChevronDown, GitBranch, Loader2,
} from 'lucide-react'
import ConversationListItem from './ConversationListItem.js'
import ConversationFilters from './ConversationFilters.js'
import { useConversationsStore } from '../../store/conversationsStore.js'
import { useConversations } from '../../hooks/useConversations.js'
import { cn } from '../../utils/cn.js'
import { api } from '../../api/client.js'

const PAGE_SIZES = [20, 50, 100, 200]

interface ChildTicket {
  id: string
  referenceId: string
  subject: string | null
  status: string
  channelOrigin: string
  displayName: string | null
}

interface ChildTicketState {
  items?: ChildTicket[]
  loading: boolean
  error?: string
}

const CHILD_STATUS: Record<string, string> = {
  open: 'bg-emerald-50 text-emerald-700',
  reopen: 'bg-blue-50 text-blue-700',
  on_hold: 'bg-amber-50 text-amber-700',
  closed: 'bg-slate-100 text-slate-600',
}

export default function ConversationList(): JSX.Element {
  const items = useConversationsStore((s) => s.items)
  const totalCount = useConversationsStore((s) => s.totalCount)
  const loading = useConversationsStore((s) => s.loading)
  const error = useConversationsStore((s) => s.error)
  const selectedId = useConversationsStore((s) => s.selectedId)
  const openTab = useConversationsStore((s) => s.openTab)
  const filters = useConversationsStore((s) => s.filters)
  const setFilters = useConversationsStore((s) => s.setFilters)
  const page = useConversationsStore((s) => s.page)
  const ratingScale = useConversationsStore((s) => s.ratingScale)
  const limit = filters.limit
  const { goToPage, refetch } = useConversations()
  const [expandedParents, setExpandedParents] = useState<Set<string>>(() => new Set())
  const [childrenByParent, setChildrenByParent] = useState<Record<string, ChildTicketState>>({})
  const childLoadsInFlight = useRef(new Set<string>())

  const totalPages = totalCount ? Math.ceil(totalCount / limit) : 1
  const startItem = totalCount ? (page - 1) * limit + 1 : 0
  const endItem = totalCount ? Math.min(page * limit, totalCount) : 0

  // Debounced search
  const [searchDraft, setSearchDraft] = useState(filters.searchQuery)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchDraft(value)
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    searchTimeoutRef.current = setTimeout(() => {
      setFilters({ searchQuery: value })
    }, 300)
  }, [setFilters])

  const handleClearSearch = useCallback(() => {
    setSearchDraft('')
    setFilters({ searchQuery: '' })
  }, [setFilters])

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    }
  }, [])

  const handlePageSizeChange = useCallback((newLimit: number) => {
    setFilters({ limit: newLimit })
  }, [setFilters])

  const visiblePages = getPageRange(page, totalPages)

  const loadChildren = useCallback((parentId: string): void => {
    if (childLoadsInFlight.current.has(parentId)) return
    childLoadsInFlight.current.add(parentId)
    setChildrenByParent((prev) => ({
      ...prev,
      [parentId]: {
        ...(prev[parentId]?.items !== undefined ? { items: prev[parentId].items } : {}),
        loading: true,
      },
    }))

    void (async () => {
      try {
        const all: ChildTicket[] = []
        // The normal inbox excludes archived tickets. Fetch both result sets so
        // the parent relationship can show every child, including archived ones.
        for (const filter of ['all', 'archived']) {
          let offset = 0
          do {
            const params = new URLSearchParams({
              parentId,
              filter,
              limit: '200',
              offset: String(offset),
            })
            const response = await api.get<{ items: ChildTicket[]; nextOffset: number | null }>(
              `/conversations?${params.toString()}`,
            )
            all.push(...(response.items ?? []))
            offset = response.nextOffset ?? -1
          } while (offset >= 0)
        }
        const uniqueById = new Map<string, ChildTicket>()
        for (const child of all) uniqueById.set(child.id, child)
        const unique = [...uniqueById.values()]
        setChildrenByParent((prev) => ({ ...prev, [parentId]: { items: unique, loading: false } }))
      } catch (error) {
        setChildrenByParent((prev) => ({
          ...prev,
          [parentId]: {
            ...(prev[parentId] ?? {}),
            loading: false,
            error: error instanceof Error ? error.message : 'Unable to load child tickets',
          },
        }))
      } finally {
        childLoadsInFlight.current.delete(parentId)
      }
    })()
  }, [])

  const toggleChildren = useCallback((parentId: string): void => {
    const isExpanded = expandedParents.has(parentId)
    setExpandedParents((prev) => {
      const next = new Set(prev)
      if (next.has(parentId)) next.delete(parentId)
      else next.add(parentId)
      return next
    })
    if (!isExpanded && !childrenByParent[parentId]?.items && !childrenByParent[parentId]?.loading) {
      loadChildren(parentId)
    }
  }, [childrenByParent, expandedParents, loadChildren])

  const parentIdsOnPage = useMemo(
    () => new Set(items.filter((item) => item.childCount > 0).map((item) => item.id)),
    [items],
  )
  const nestedChildIds = useMemo(
    () => new Set(
      items
        .filter((item) => item.parentId && parentIdsOnPage.has(item.parentId) && expandedParents.has(item.parentId) && Array.isArray(childrenByParent[item.parentId]?.items))
        .map((item) => item.id),
    ),
    [items, parentIdsOnPage, expandedParents, childrenByParent],
  )
  const visibleItems = useMemo(
    () => items.filter((item) => !nestedChildIds.has(item.id)),
    [items, nestedChildIds],
  )
  const keyboardItems = useMemo(() => visibleItems.flatMap((item) => {
    const nested = expandedParents.has(item.id) ? childrenByParent[item.id]?.items ?? [] : []
    return [item, ...nested.map((child) => ({
      id: child.id,
      displayName: child.displayName ?? '',
      channelOrigin: child.channelOrigin,
    }))]
  }), [visibleItems, expandedParents, childrenByParent])

  // Shared 1-second timer — drives all SLA/OLA mini timer badges in list items
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1_000)
    return () => clearInterval(id)
  }, [])

  // Keyboard navigation
  const listRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = listRef.current
    if (!el) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return

      const currentIndex = keyboardItems.findIndex((it) => it.id === selectedId)

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault()
          const nextIndex = currentIndex + 1
          if (nextIndex < keyboardItems.length) {
            const it = keyboardItems[nextIndex]!
            openTab({ id: it.id, displayName: it.displayName, channelOrigin: it.channelOrigin })
          }
          break
        }
        case 'ArrowUp': {
          e.preventDefault()
          const prevIndex = currentIndex - 1
          if (prevIndex >= 0) {
            const it = keyboardItems[prevIndex]!
            openTab({ id: it.id, displayName: it.displayName, channelOrigin: it.channelOrigin })
          }
          break
        }
        case 'Enter': {
          if (currentIndex >= 0 && keyboardItems[currentIndex]) {
            e.preventDefault()
            window.dispatchEvent(new CustomEvent('conversation:focus-composer'))
          }
          break
        }
        case 'PageDown': {
          e.preventDefault()
          if (!loading && page < totalPages) {
            void goToPage(page + 1)
          }
          break
        }
        case 'PageUp': {
          e.preventDefault()
          if (!loading && page > 1) {
            void goToPage(page - 1)
          }
          break
        }
      }
    }

    el.addEventListener('keydown', handleKeyDown)
    return () => el.removeEventListener('keydown', handleKeyDown)
  }, [keyboardItems, selectedId, openTab, page, totalPages, loading, goToPage])

  return (
    <aside ref={listRef} tabIndex={0} className="flex w-80 shrink-0 flex-col rounded-2xl border border-gray-200/60 bg-white shadow-sm overflow-hidden outline-none focus:ring-0">
      {/* Header */}
      <div className="flex h-16 shrink-0 items-center justify-between border-b border-gray-100 px-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#16273F]">
            <MessageSquarePlus className="h-4 w-4 text-white" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-gray-900">Inbox</h2>
            <p className="text-[11px] text-gray-400">{totalCount?.toLocaleString() ?? 0} conversations</p>
          </div>
        </div>
        <button
          onClick={() => {
            window.dispatchEvent(new CustomEvent('conversations:new'))
          }}
          className="flex h-8 items-center gap-1 rounded-lg bg-[#16273F] px-3 text-xs font-medium text-white transition-all hover:bg-[#1e3657] active:scale-[0.97]"
        >
          <Plus className="h-3.5 w-3.5" />
          New
        </button>
      </div>

      {/* Search bar */}
      <div className="shrink-0 border-b border-gray-100 px-3 py-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchDraft}
            onChange={handleSearchChange}
            className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-8 pr-8 text-xs text-gray-700 outline-none transition-all placeholder:text-gray-400 focus:border-gray-300 focus:bg-white"
          />
          {searchDraft && (
            <button
              onClick={handleClearSearch}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <span className="text-xs">×</span>
            </button>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div className="shrink-0 border-b border-gray-100">
        <ConversationFilters />
      </div>

      {/* Scrollable list */}
      <div className="flex-1 overflow-y-auto bg-gray-50/50">
        {error && (
          <div role="alert" className="m-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            <div className="mb-2">{error}</div>
            <button
              onClick={() => void refetch()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-red-700 shadow-sm transition-all hover:bg-red-50"
            >
              <RefreshCw className="h-3 w-3" />
              Retry
            </button>
          </div>
        )}

        {loading && items.length === 0 && !error && (
          <div data-testid="list-loading-skeleton" className="space-y-2 p-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-xl bg-gray-100" />
            ))}
          </div>
        )}

        {!loading && items.length === 0 && !error && (
          <div className="flex flex-col items-center justify-center p-8 text-center text-sm text-gray-500">
            <MessageSquarePlus className="h-10 w-10 text-gray-200" />
            <p className="mt-3 font-medium text-gray-600">No conversations</p>
            <p className="text-xs text-gray-400">
              {filters.searchQuery ? 'Try a different search' : 'Adjust filters to see more'}
            </p>
          </div>
        )}

        <ul>
          {visibleItems.map((item, idx) => {
            const expanded = expandedParents.has(item.id)
            const childState = childrenByParent[item.id]
            const isLast = idx === visibleItems.length - 1
            return (
              <li
                key={item.id}
                className={cn(!isLast && 'border-b border-gray-100/60')}
              >
                <ConversationListItem
                  entry={item}
                  selected={item.id === selectedId}
                  isFirst={idx === 0}
                  isLast={isLast && item.childCount === 0}
                  now={now}
                  ratingScale={ratingScale}
                  onClick={() => openTab({ id: item.id, displayName: item.displayName, channelOrigin: item.channelOrigin })}
                />
                {item.childCount > 0 && (
                  <div className="border-t border-violet-100/70 bg-violet-50/40">
                    <button
                      type="button"
                      aria-expanded={expanded}
                      onClick={() => toggleChildren(item.id)}
                      className="flex w-full items-center gap-2 px-4 py-2 text-left text-[11px] font-semibold text-violet-700 transition-colors hover:bg-violet-50"
                    >
                      {expanded ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                      <GitBranch className="h-3.5 w-3.5 shrink-0" />
                      <span>Child tickets</span>
                      <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] tabular-nums text-violet-700 ring-1 ring-inset ring-violet-100">{item.childCount}</span>
                      {childState?.loading && <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin text-violet-500" />}
                    </button>
                    {expanded && (
                      <div className="space-y-1 px-3 pb-2 pl-9">
                        {childState?.error && (
                          <div className="flex items-center justify-between gap-2 px-2 py-1 text-[11px] text-red-600">
                            <span>Could not load child tickets.</span>
                            <button type="button" onClick={() => loadChildren(item.id)} className="font-semibold underline">Retry</button>
                          </div>
                        )}
                        {childState?.loading && !childState.items && (
                          <div className="px-2 py-2 text-[11px] text-slate-400">Loading child tickets…</div>
                        )}
                        {childState?.items?.map((child) => (
                          <button
                            key={child.id}
                            type="button"
                            onClick={() => openTab({ id: child.id, displayName: child.displayName ?? '', channelOrigin: child.channelOrigin })}
                            title={child.subject || '(No subject)'}
                            className={cn(
                              'flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left transition-colors',
                              child.id === selectedId
                                ? 'border-[#3366FF] bg-blue-50'
                                : 'border-violet-100 bg-white hover:border-violet-300 hover:bg-violet-50/60',
                            )}
                          >
                            <GitBranch className="h-3 w-3 shrink-0 text-violet-400" />
                            <span className="shrink-0 font-mono text-[10px] font-semibold text-slate-600">#{child.referenceId}</span>
                            <span className="min-w-0 flex-1 truncate text-[11px] text-slate-700">{child.subject || '(No subject)'}</span>
                            <span className={cn('shrink-0 rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase', CHILD_STATUS[child.status] ?? 'bg-slate-100 text-slate-600')}>
                              {child.status.replace('_', ' ')}
                            </span>
                          </button>
                        ))}
                        {childState?.items?.length === 0 && !childState.loading && !childState.error && (
                          <div className="px-2 py-2 text-[11px] text-slate-400">No child tickets found.</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      </div>

      {/* Footer: Pagination + page size */}
      {totalCount !== null && totalCount > 0 && (
        <div className="shrink-0 border-t border-gray-100 bg-white px-3 py-2">
          {/* Count indicator */}
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-[10px] text-gray-400">
              {startItem}-{endItem} of {totalCount.toLocaleString()}
            </span>
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-gray-400">Show:</span>
              {PAGE_SIZES.map((sz) => (
                <button
                  key={sz}
                  onClick={() => handlePageSizeChange(sz)}
                  className={cn(
                    'rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors',
                    limit === sz
                      ? 'bg-[#16273F] text-white'
                      : 'text-gray-500 hover:bg-gray-100',
                  )}
                >
                  {sz}
                </button>
              ))}
            </div>
          </div>

          {/* Page buttons */}
          <div className="flex items-center justify-center gap-1">
            <button
              onClick={() => void goToPage(page - 1)}
              disabled={page <= 1 || loading}
              className="flex h-6 w-6 items-center justify-center rounded-md text-gray-500 transition-all hover:bg-gray-100 disabled:opacity-30"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>

            {visiblePages.map((p, i) =>
              p === -1 ? (
                <span key={`ellipsis-${i}`} className="px-1 text-[10px] text-gray-400">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => void goToPage(p)}
                  disabled={loading}
                  className={cn(
                    'flex h-6 min-w-[24px] items-center justify-center rounded-md px-1 text-[10px] font-medium transition-all',
                    p === page
                      ? 'bg-[#16273F] text-white'
                      : 'text-gray-600 hover:bg-gray-100',
                  )}
                >
                  {p}
                </button>
              )
            )}

            <button
              onClick={() => void goToPage(page + 1)}
              disabled={page >= totalPages || loading}
              className="flex h-6 w-6 items-center justify-center rounded-md text-gray-500 transition-all hover:bg-gray-100 disabled:opacity-30"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </aside>
  )
}

function getPageRange(current: number, total: number): number[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)

  const pages: number[] = []
  pages.push(1)

  if (current > 4) pages.push(-1)

  const start = Math.max(2, current - 2)
  const end = Math.min(total - 1, current + 2)
  for (let i = start; i <= end; i++) pages.push(i)

  if (current < total - 3) pages.push(-1)
  if (total > 1) pages.push(total)

  return pages
}
